import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, WavesIcon, CalendarDaysIcon, UsersIcon, LayoutGridIcon, UserCheckIcon, CreditCardIcon, CheckCircle2Icon, CalendarCheckIcon, BellIcon, PartyPopperIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { LoisirsDesktopMock } from './LoisirsDesktopMock';
import { LoisirsPhoneMock } from './LoisirsPhoneMock';
type Bilingual = {
  en: string;
  fr: string;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO_SWIMMER = 'https://cdn.magicpatterns.com/patterns/generated-images/b78882f6-12ee-4544-b668-5f9e6d917737.jpg';
const CAPABILITIES: {
  icon: typeof CalendarDaysIcon;
  label: Bilingual;
}[] = [{
  icon: CalendarDaysIcon,
  label: {
    fr: 'Activités et inscriptions',
    en: 'Activities & registrations'
  }
}, {
  icon: UsersIcon,
  label: {
    fr: 'Camps de jour',
    en: 'Day camps'
  }
}, {
  icon: LayoutGridIcon,
  label: {
    fr: 'Plateaux et réservations',
    en: 'Facilities & bookings'
  }
}, {
  icon: UserCheckIcon,
  label: {
    fr: 'Accès et présences',
    en: 'Access & attendance'
  }
}, {
  icon: CreditCardIcon,
  label: {
    fr: 'Paiements et remboursements',
    en: 'Payments & refunds'
  }
}];
const STATUSES: {
  icon: typeof CheckCircle2Icon;
  label: Bilingual;
  tone: string;
}[] = [{
  icon: CheckCircle2Icon,
  label: {
    fr: 'Carte numérique BCITI validée',
    en: 'BCITI Digital Card validated'
  },
  tone: 'text-[#16A34A]'
}, {
  icon: CalendarCheckIcon,
  label: {
    fr: 'Inscription confirmée',
    en: 'Registration confirmed'
  },
  tone: 'text-[#4F46E5]'
}, {
  icon: CreditCardIcon,
  label: {
    fr: 'Paiement effectué',
    en: 'Payment completed'
  },
  tone: 'text-[#4F46E5]'
}, {
  icon: BellIcon,
  label: {
    fr: 'Rappel envoyé',
    en: 'Reminder sent'
  },
  tone: 'text-[#4F46E5]'
}, {
  icon: PartyPopperIcon,
  label: {
    fr: 'Prête pour l’été !',
    en: 'Ready for summer!'
  },
  tone: 'text-[#4F46E5]'
}];
export function LoisirsHero() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const reduce = useReducedMotion();
  return <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-[43fr_57fr] gap-12 lg:gap-8 items-center" data-id="element-4230" data-mp="bi8sdrlyvrk5">
      {/* LEFT — message */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: EASE
    }} className="min-w-0" data-id="element-4231" data-mp="ki5atbhrju6r">
        <p className="flex items-center gap-3 font-sans text-xl font-semibold text-[#1E1B4B]" data-id="element-4232" data-mp="uei62dsyig3m">
          <span className="w-10 h-10 rounded-xl bg-[#EEEDFD] flex items-center justify-center" data-id="element-4233" data-mp="z5gr1qow42d7">
            <WavesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-4234" data-mp="145l4t8yeyizq" />
          </span>
          {isFR ? 'Loisirs ATNA' : 'ATNA Recreation'}
        </p>

        <h1 className="font-sans font-semibold text-[2.9rem] sm:text-6xl xl:text-[4.4rem] leading-[1.02] tracking-[-0.035em] text-[#121212] mt-8" data-id="element-4235" data-mp="1d5rgqqegraws">
          {isFR ? 'Trouvez. Inscrivez-vous.' : 'Find. Register.'}
          <span className="block" data-id="element-4236" data-mp="2so7jj9dt7tro">{isFR ? 'Participez.' : 'Participate.'}</span>
          <span className="block text-[#4F46E5]" data-id="element-4237" data-mp="2on43omvrhxqg">{isFR ? 'Plus simplement.' : 'Made simple.'}</span>
        </h1>

        <p className="font-sans text-lg text-[#5B6478] leading-relaxed mt-7 max-w-[560px]" data-id="element-4238" data-mp="2joaq6ugh66qa">
          {isFR ? 'Une plateforme intelligente pour gérer activités, camps, inscriptions, réservations, ressources, paiements, accès et présences — de la découverte jusqu’aux opérations.' : 'An intelligent platform to manage activities, camps, registrations, bookings, resources, payments, access and attendance — from discovery to operations.'}
        </p>

        <Link to="/demo" onClick={() => trackEvent('CTA Clicked', {
        location: 'loisirs_hero',
        cta: 'see_in_action'
      })} className="group mt-9 inline-flex items-center gap-3 rounded-full bg-[#4F46E5] px-7 py-4 font-sans text-base font-medium text-white shadow-[0_10px_24px_rgba(79,70,229,0.25)] transition-[background-color,transform] duration-150 ease-out hover:bg-[#4338CA] active:scale-[0.98]" data-id="element-4239" data-mp="32l2ghnufpm3u">
          {isFR ? 'Voir Loisirs ATNA en action' : 'See ATNA Recreation in action'}
          <ArrowRightIcon className="w-5 h-5 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-4240" data-mp="3au6k64ikqag5" />
        </Link>

        <ul className="mt-12 flex lg:grid lg:grid-cols-5 gap-5 overflow-x-auto pb-2 -mx-1 px-1 snap-x" data-id="element-4241" data-mp="2h0phobskmde">
          {CAPABILITIES.map(({
          icon: Icon,
          label
        }) => <li key={label.en} className="shrink-0 w-[96px] lg:w-auto snap-start flex flex-col items-center text-center" data-id="element-4242" data-mp="3otpqqsvqldqc">
              <span className="w-12 h-12 rounded-2xl bg-[#F3F2FE] flex items-center justify-center" data-id="element-4243" data-mp="3tr7pxrjycel5">
                <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4244" data-mp="lbfr7lphmt4x" />
              </span>
              <span className="font-sans text-[13px] text-[#121212] leading-snug mt-3" data-id="element-4245" data-mp="zj13bzwgffox">{pick(label)}</span>
            </li>)}
        </ul>
      </motion.div>

      {/* RIGHT — product experience */}
      <div className="relative min-w-0 flex flex-col items-center gap-8 lg:block lg:h-[700px]" data-id="element-4246" data-mp="198km75k639js">
        {/* Desktop interface */}
        <motion.div initial={{
        opacity: 0,
        x: 16
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: EASE
      }} className="order-2 w-full lg:absolute lg:top-0 lg:right-0 lg:w-[80%]" data-id="element-4247" data-mp="14aslbm1jod3g">
          <LoisirsDesktopMock isFR={isFR} data-id="element-4248" data-mp="qicq37evxjd3" />
        </motion.div>

        {/* Lifestyle photo + journey statuses */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.25,
        ease: EASE
      }} className="order-3 relative w-full h-[300px] lg:absolute lg:bottom-0 lg:right-0 lg:w-[72%] lg:h-[330px] rounded-3xl overflow-hidden" data-id="element-4249" data-mp="liqd8z2yy54t">
          <img src={PHOTO_SWIMMER} alt={isFR ? 'Une enfant souriante dans la piscine' : 'A smiling child in the pool'} className="w-full h-full object-cover object-left" data-id="element-4250" data-mp="24zw0s3lkhn77" />
          <ul className="absolute right-3 bottom-3 top-3 flex flex-col justify-end gap-2" data-id="element-4251" data-mp="2e140mq3qew3e">
            {STATUSES.map(({
            icon: Icon,
            label,
            tone
          }, i) => <motion.li key={label.en} initial={{
            opacity: 0,
            x: 12
          }} animate={reduce ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 1,
            x: 0,
            y: [0, -3, 0]
          }} transition={{
            opacity: {
              duration: 0.3,
              delay: 1.9 + i * 0.12,
              ease: EASE
            },
            x: {
              duration: 0.3,
              delay: 1.9 + i * 0.12,
              ease: EASE
            },
            y: {
              duration: 4,
              delay: 2.6 + i * 0.4,
              repeat: Infinity,
              ease: 'easeInOut'
            }
          }} className="flex items-center gap-2.5 rounded-full bg-white/95 pl-2 pr-4 py-1.5 shadow-[0_6px_18px_rgba(18,18,40,0.12)]" data-id="element-4252" data-mp="1lbeww4zn0hy3" data-mpi={i}>
                <span className="w-6 h-6 rounded-full bg-[#F5F6F8] flex items-center justify-center" data-id="element-4253" data-mp="zzpij5eo6b5h">
                  <Icon className={`w-3.5 h-3.5 ${tone}`} strokeWidth={2} aria-hidden="true" data-id="element-4254" data-mp="v1wbka5jld45" />
                </span>
                <span className="font-sans text-[12.5px] font-medium text-[#121212] whitespace-nowrap" data-id="element-4255" data-mp="q44hpalc7gxb">
                  {pick(label)}
                </span>
              </motion.li>)}
          </ul>
        </motion.div>

        {/* ATNA phone */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: EASE
      }} className="order-1 lg:absolute lg:left-0 lg:top-[90px] z-10" data-id="element-4256" data-mp="c9lmpk7q3fp1">
          <LoisirsPhoneMock isFR={isFR} data-id="element-4257" data-mp="3tiqtjz8to9bx" />
        </motion.div>
      </div>
    </div>;
}